/**
 * livekitClient.js — Thin Government-Shield-shaped wrapper over
 * the livekit-client v2 Room API.
 *
 * **Phase 2 scope**: plumbing only. No UI, no wiring into
 * VoiceCallModal, no group-call panel. This file exports just
 * enough to:
 *
 *   1. Fetch a room token from the existing backend
 *      `POST /api/voice/room-token` endpoint (landed in Phase 1).
 *   2. Instantiate a Room preconfigured with our
 *      ExternalE2EEKeyProvider + vendored E2EE worker.
 *   3. Connect to the LiveKit SFU, enable E2EE, and expose the
 *      underlying Room instance to the caller.
 *   4. Wire event-handler callbacks (participant join/leave,
 *      disconnect, track subscribed, E2EE errors) so Phase 3 has
 *      a stable surface to hook UI into.
 *
 * **Feature-flag behaviour**: every exported function is a
 * no-op-friendly path when the backend reports LiveKit is
 * disabled. `isLiveKitReady()` answers whether the backend has
 * the env vars set — the UI gates group-call affordances on this
 * so we never strand a user inside a half-working call modal.
 *
 * **E2EE key flow (documented here so the Phase 3 implementer
 * doesn't reinvent it)**:
 *   - Initiator calls randomRoomKey() → wraps bytes per-peer via
 *     Sealed Sender → relays through existing messaging channel.
 *   - Every participant (including initiator) calls
 *     installPassphrase(provider, bytes) which drives LiveKit's
 *     HKDF ratchet. SFU sees only ciphertext.
 *   - SAS code is deriveSFrameSAS(bytes) — identical on every
 *     client because the raw bytes are identical.
 */

import axios from 'axios';
import { Room, RoomEvent, Track } from 'livekit-client';
import { createSealedE2EEKeyProvider, installPassphrase } from './livekitKeyProvider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Relative path (served from /public) to the vendored LiveKit
 * E2EE worker bundle. We copy the file from
 * node_modules/livekit-client/dist/livekit-client.e2ee.worker.js
 * during Phase 2 setup so CRA's webpack doesn't have to understand
 * Worker URLs in node_modules. If the LiveKit SDK is upgraded,
 * re-copy that file over /public/livekit-e2ee-worker.js.
 */
export const E2EE_WORKER_URL = '/livekit-e2ee-worker.js';

/**
 * Cheap client-side probe. Does not hit the server — it assumes
 * the backend returned enabled:false on the last token fetch and
 * caches that state. Callers who haven't fetched a token yet
 * should call fetchRoomToken() first and read .enabled on the
 * response. This helper is for UI-gating code that already has
 * the flag.
 */
let _cachedEnabled = null;
export const isLiveKitReady = () => _cachedEnabled === true;

/**
 * Call POST /api/voice/room-token.
 *
 * @param {{ peerUserIds: string[], authToken: string }} params
 * @returns {Promise<{ enabled: boolean, url?: string, token?: string, room?: string, reason?: string }>}
 *
 * The response shape mirrors the backend's pydantic
 * `RoomTokenResponse`. When `enabled:false`, `reason` names the
 * missing env vars so the caller can surface an actionable error
 * toast instead of a generic "call failed".
 */
export const fetchRoomToken = async ({ peerUserIds, authToken }) => {
  if (!Array.isArray(peerUserIds) || peerUserIds.length === 0) {
    throw new Error('livekitClient: peerUserIds must be a non-empty array');
  }
  if (!authToken) {
    throw new Error('livekitClient: authToken required');
  }
  const { data } = await axios.post(
    `${API}/voice/room-token`,
    { peer_user_ids: peerUserIds },
    { headers: { Authorization: `Bearer ${authToken}` } },
  );
  _cachedEnabled = Boolean(data?.enabled);
  return data;
};

/**
 * Build a LiveKit Room with GS's E2EE provider pre-wired.
 *
 * The returned object is:
 *   { room, provider, worker }
 *
 * Caller's lifecycle responsibility:
 *   - `room.disconnect()` on unmount
 *   - `worker.terminate()` after disconnect (worker is NOT owned
 *     by Room — it's passed in and survives a reconnect)
 */
export const buildRoom = ({ adaptiveStream = true, dynacast = false } = {}) => {
  const provider = createSealedE2EEKeyProvider();
  const worker = new Worker(E2EE_WORKER_URL);

  const room = new Room({
    // Voice calls are audio-only in GS; video is out of scope
    // for the current migration and would change the cost
    // envelope by 10-20×.
    adaptiveStream,
    dynacast,
    // E2EE MUST be on from the first frame. LiveKit will refuse
    // to publish unencrypted tracks once this option is set.
    e2ee: {
      keyProvider: provider,
      worker,
    },
    // Leave codec selection to LiveKit defaults (Opus for audio).
    publishDefaults: {
      red: false,
      // SFrame adds ~4 bytes per frame; stick with the default
      // bitrate. Tuning is a Phase 3+ concern.
    },
  });

  return { room, provider, worker };
};

/**
 * Full connect helper. Installs the shared secret + connects.
 *
 * @param {Object} params
 * @param {Room}   params.room
 * @param {Object} params.provider ExternalE2EEKeyProvider from buildRoom
 * @param {string} params.url      wss://... from fetchRoomToken
 * @param {string} params.token    LiveKit JWT from fetchRoomToken
 * @param {Uint8Array} params.sharedKey 32-byte secret (see randomRoomKey)
 * @returns {Promise<Room>}
 *
 * Installs the key BEFORE connecting so the first frame published
 * is already encrypted — there's no cleartext window between
 * connect and key-set.
 */
export const connectToRoom = async ({ room, provider, url, token, sharedKey }) => {
  if (!room || !provider) {
    throw new Error('livekitClient: room + provider are required');
  }
  if (!url || !token) {
    throw new Error('livekitClient: url + token are required');
  }
  if (!sharedKey) {
    throw new Error('livekitClient: sharedKey is required — call randomRoomKey() upstream');
  }
  await installPassphrase(provider, sharedKey);
  await room.connect(url, token);
  return room;
};

/**
 * Subscribe to the minimal event surface Phase 3 will need.
 * Handlers are optional — pass what you care about.
 *
 * @param {Room} room
 * @param {{
 *   onParticipantConnected?: (p) => void,
 *   onParticipantDisconnected?: (p) => void,
 *   onDisconnected?: (reason) => void,
 *   onTrackSubscribed?: (track, publication, participant) => void,
 *   onE2EEError?: (err) => void,
 *   onActiveSpeakersChanged?: (speakers) => void,
 * }} handlers
 * @returns {() => void} unsubscribe function
 */
export const subscribeRoomEvents = (room, handlers = {}) => {
  const h = handlers;
  const wired = [];

  const bind = (event, fn) => {
    if (!fn) return;
    room.on(event, fn);
    wired.push([event, fn]);
  };

  bind(RoomEvent.ParticipantConnected, h.onParticipantConnected);
  bind(RoomEvent.ParticipantDisconnected, h.onParticipantDisconnected);
  bind(RoomEvent.Disconnected, h.onDisconnected);
  bind(RoomEvent.TrackSubscribed, h.onTrackSubscribed);
  bind(RoomEvent.EncryptionError, h.onE2EEError);
  bind(RoomEvent.ActiveSpeakersChanged, h.onActiveSpeakersChanged);

  return () => {
    wired.forEach(([event, fn]) => room.off(event, fn));
  };
};

/**
 * Enable the local mic track on a connected Room. Split out from
 * connectToRoom() so a future "silent join" flow (mute-until-
 * verified) is a one-line change.
 */
export const enableLocalMicrophone = async (room) => {
  if (!room?.localParticipant) {
    throw new Error('livekitClient: room has no localParticipant');
  }
  await room.localParticipant.setMicrophoneEnabled(true);
};

/**
 * Convenience: attach a subscribed audio track to a DOM element.
 * Re-exported from livekit-client.Track.Source for downstream
 * callers that want to filter publications by kind without
 * pulling livekit-client directly into their component.
 */
export const TrackSource = Track.Source;
